import {
  formatAttachmentPhrase,
  formatTextLimitPhrase,
} from './distributionChannelSummary';

import type {
  AttachmentOption,
  DistributionChannel,
  DistributionFormatType,
} from '@src/types.ts/distribution-channel';

const FORMAT_TYPE_ORDER: DistributionFormatType[] = [
  'feed_post',
  'short_form_video',
  'long_form_video',
  'photo_carousel',
  'story',
  'article',
];

const FORMAT_TYPE_LABEL: Record<DistributionFormatType, string> = {
  feed_post: 'Feed Post',
  short_form_video: 'Short-Form Video',
  long_form_video: 'Long-Form Video',
  photo_carousel: 'Photo Carousel',
  story: 'Story',
  article: 'Article',
};

export interface KnownEnvelope {
  formatType: DistributionFormatType;
  label: string;
}

export const getFormatTypeLabel = (formatType: DistributionFormatType): string =>
  FORMAT_TYPE_LABEL[formatType];

export const getKnownEnvelopes = (): KnownEnvelope[] =>
  FORMAT_TYPE_ORDER.map(formatType => ({
    formatType,
    label: FORMAT_TYPE_LABEL[formatType],
  }));

type Aspect =
  | 'text'
  | 'image'
  | 'image_aspect_ratios'
  | 'image_resolution'
  | 'video'
  | 'video_aspect_ratios'
  | 'video_resolution_min'
  | 'video_resolution_max'
  | 'document'
  | 'link_share'
  | 'poll';

const ASPECT_ORDER: Aspect[] = [
  'text',
  'image',
  'image_aspect_ratios',
  'image_resolution',
  'video',
  'video_aspect_ratios',
  'video_resolution_min',
  'video_resolution_max',
  'document',
  'link_share',
  'poll',
];

const ASPECT_LABEL: Record<Aspect, string> = {
  text: 'Text limit',
  image: 'Images',
  image_aspect_ratios: 'Image aspect ratios',
  image_resolution: 'Image resolution',
  video: 'Video',
  video_aspect_ratios: 'Video aspect ratios',
  video_resolution_min: 'Video resolution min',
  video_resolution_max: 'Video resolution max',
  document: 'Documents',
  link_share: 'Link share',
  poll: 'Poll',
};

const formatDimensions = (dim: string | null): string | null => {
  if (!dim) return null;
  // Convert "1920x1080" → "1920×1080" (proper × glyph for display).
  const match = dim.match(/^(\d+)x(\d+)$/i);
  return match ? `${match[1]}×${match[2]}` : dim;
};

const formatImageResolutionPhrase = (
  attachment: Extract<AttachmentOption, { type: 'image' }>,
): string | null => {
  const max = formatDimensions(attachment.maxDimensions);
  const min = formatDimensions(attachment.minDimensions);
  if (max && min) return `${min} – ${max}`;
  if (max) return `≤${max}`;
  if (min) return `≥${min}`;
  return null;
};

const formatVideoDimensionPhrase = (
  attachment: Extract<AttachmentOption, { type: 'video' }>,
  bound: 'min' | 'max',
): string | null =>
  formatDimensions(
    bound === 'min' ? attachment.minDimensions : attachment.maxDimensions,
  );

// Canonical display order for common aspect ratios — landscape, square,
// then portrait in increasing tallness. Anything not listed sorts after,
// alphabetically, so display is stable across channels.
const ASPECT_RATIO_ORDER: string[] = [
  '16:9',
  '4:3',
  '3:2',
  '1:1',
  '4:5',
  '2:3',
  '3:4',
  '9:16',
];

const aspectRatioRank = (ratio: string): number => {
  const i = ASPECT_RATIO_ORDER.indexOf(ratio);
  return i === -1 ? ASPECT_RATIO_ORDER.length : i;
};

const formatAspectRatiosPhrase = (
  attachment: Extract<AttachmentOption, { type: 'image' | 'video' }>,
): string | null => {
  if (!attachment.aspectRatios || attachment.aspectRatios.length === 0) {
    return null;
  }
  const sorted = [...attachment.aspectRatios].sort((a, b) => {
    const rankDiff = aspectRatioRank(a) - aspectRatioRank(b);
    return rankDiff !== 0 ? rankDiff : a.localeCompare(b);
  });
  return sorted.join(', ');
};

export type ComparisonRow = {
  formatType: DistributionFormatType;
  formatLabel: string;
  aspect: Aspect;
  aspectLabel: string;
  cellsByChannelId: Record<string, string>;
};

const cellForAspect = (
  format: DistributionChannel['formats'][number],
  aspect: Aspect,
): string | null => {
  if (aspect === 'text') {
    return formatTextLimitPhrase(format.textMaxChars);
  }

  if (aspect === 'image_aspect_ratios' || aspect === 'image_resolution') {
    const image = format.attachments.find(a => a.type === 'image');
    if (!image) return null;
    return aspect === 'image_aspect_ratios'
      ? formatAspectRatiosPhrase(image)
      : formatImageResolutionPhrase(image);
  }

  if (
    aspect === 'video_aspect_ratios' ||
    aspect === 'video_resolution_min' ||
    aspect === 'video_resolution_max'
  ) {
    const video = format.attachments.find(a => a.type === 'video');
    if (!video) return null;
    if (aspect === 'video_aspect_ratios') {
      return formatAspectRatiosPhrase(video);
    }
    return formatVideoDimensionPhrase(
      video,
      aspect === 'video_resolution_min' ? 'min' : 'max',
    );
  }

  const attachment = format.attachments.find(a => a.type === aspect);
  if (!attachment) return null;
  return formatAttachmentPhrase(attachment);
};

export interface FormatDetailRow {
  aspect: Aspect;
  label: string;
  value: string;
}

/**
 * Per-aspect detail rows for a single format, in the same aspect order used
 * by the Compare table. Empty aspects (no value) are dropped. Used by the
 * Summary-mode tooltip so it shows the same breakdown as the Details mode.
 */
export const getFormatDetailRows = (
  format: DistributionChannel['formats'][number],
): FormatDetailRow[] => {
  const rows: FormatDetailRow[] = [];
  for (const aspect of ASPECT_ORDER) {
    const value = cellForAspect(format, aspect);
    if (value !== null) {
      rows.push({ aspect, label: ASPECT_LABEL[aspect], value });
    }
  }
  return rows;
};

/**
 * Build a flat list of (formatType, aspect) rows comparing the supplied
 * channels. A row is included only if at least one channel supports that
 * combination — empty cells render as a placeholder, not missing rows.
 */
export const buildComparisonRows = (
  channels: DistributionChannel[],
): ComparisonRow[] => {
  const rows: ComparisonRow[] = [];

  for (const formatType of FORMAT_TYPE_ORDER) {
    for (const aspect of ASPECT_ORDER) {
      const cellsByChannelId: Record<string, string> = {};
      let anyHit = false;

      for (const channel of channels) {
        const format = channel.formats.find(f => f.formatType === formatType);
        if (!format) continue;
        const cell = cellForAspect(format, aspect);
        if (cell !== null) {
          cellsByChannelId[channel.id] = cell;
          anyHit = true;
        }
      }

      if (anyHit) {
        rows.push({
          formatType,
          formatLabel: FORMAT_TYPE_LABEL[formatType],
          aspect,
          aspectLabel: ASPECT_LABEL[aspect],
          cellsByChannelId,
        });
      }
    }
  }

  return rows;
};
